import { useState } from 'react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import type { SuspiciousLocationAlert } from '../../lib/api';

interface SuspiciousLocationsTableProps {
  items: SuspiciousLocationAlert[];
  onDismiss?: (alert: SuspiciousLocationAlert) => void;
}

export function SuspiciousLocationsTable({
  items,
  onDismiss,
}: SuspiciousLocationsTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (key: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const formatDate = (dateStr: string) => {
    try {
      // dateStr is YYYY-MM-DD already in Pacific timezone from the API
      // Parse manually to avoid UTC conversion issues
      // (new Date("YYYY-MM-DD") interprets as UTC, causing off-by-one day errors)
      const [year, month, day] = dateStr.split('-').map(Number);
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      // Create date at noon local time just to get day of week
      const dateObj = new Date(year, month - 1, day, 12, 0, 0);
      return `${dayNames[dateObj.getDay()]}, ${monthNames[month - 1]} ${day}, ${year}`;
    } catch {
      return dateStr;
    }
  };

  const formatTime = (timestamp: string) => {
    try {
      return new Date(timestamp).toLocaleTimeString('en-US', {
        timeZone: 'America/Los_Angeles',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return '—';
    }
  };

  const handleAction = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  if (items.length === 0) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <span className="text-4xl mb-4 block">✅</span>
          <p className="text-sage-600 font-medium">No suspicious locations</p>
          <p className="text-sage-500 text-sm mt-1">All service logs are within expected range</p>
        </div>
      </div>
    );
  }

  return (
    <div className="divide-y divide-sage-100">
      {items.map((alert) => {
        const rowKey = `${alert.employeeId}:${alert.alertDate}`;
        const isExpanded = expandedRows.has(rowKey);

        return (
          <div key={rowKey} className="bg-white">
            {/* Main row */}
            <div
              className={`px-6 py-4 flex items-center gap-4 cursor-pointer hover:bg-sage-50 transition-colors ${
                alert.isDismissed ? 'opacity-60' : ''
              }`}
              onClick={() => toggleRow(rowKey)}
            >
              {/* Expand icon */}
              <button className="text-sage-400 hover:text-forest-700 transition-colors">
                <svg
                  className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* Employee & Date */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-amber-500">📍</span>
                  <p className={`font-medium text-forest-900 ${alert.isDismissed ? 'line-through' : ''}`}>
                    {alert.employeeName || alert.employeeId}
                  </p>
                  {alert.isDismissed && (
                    <Badge variant="default" size="sm">Dismissed</Badge>
                  )}
                </div>
                <p className="text-sm text-sage-500 mt-0.5">{formatDate(alert.alertDate)}</p>
              </div>

              {/* Log count */}
              <div className="text-center">
                <Badge variant="warning" size="sm">
                  {alert.serviceLogCount} {alert.serviceLogCount === 1 ? 'log' : 'logs'}
                </Badge>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                {alert.isDismissed ? (
                  <div className="text-right">
                    <p className="text-xs text-sage-500">Dismissed by {alert.dismissedBy}</p>
                    <p className="text-xs text-sage-400 truncate max-w-[150px]" title={alert.dismissalComment || ''}>
                      "{alert.dismissalComment}"
                    </p>
                  </div>
                ) : (
                  onDismiss && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleAction(e, () => onDismiss(alert))}
                      className="text-sage-600 hover:text-forest-700"
                    >
                      Dismiss
                    </Button>
                  )
                )}
              </div>
            </div>

            {/* Expanded detail */}
            {isExpanded && (
              <div className="px-6 pb-4 bg-sage-50">
                <div className="ml-9 border-l-2 border-sage-200 pl-4">
                  <p className="text-xs font-medium text-sage-600 uppercase tracking-wider mb-2">
                    Service Logs ({alert.logs.length})
                  </p>
                  <div className="space-y-2">
                    {alert.logs.map((log) => (
                      <div
                        key={log.id}
                        className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-sage-200"
                      >
                        <div>
                          <p className="text-sm font-medium text-forest-800">
                            {log.clientName || log.clientId}
                          </p>
                          <p className="text-xs text-sage-500">
                            {formatTime(log.timestamp)}
                          </p>
                        </div>
                        <Badge variant="danger" size="sm">
                          {log.distanceFromClient?.toFixed(1) || '?'} mi away
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
